/**
 * Every category of the campaign (92:2's panel, one list for all of them): its icon, name,
 * account count or "sem uso" when deactivated, its values as icon chips, an add-value
 * affordance and -- `canWrite` only -- icon pickers, rename, delete and the active switch.
 *
 * The locked alignment category (always first) and its four fixed values take no rename,
 * deactivation or delete; a value added to it names the alignment it counts as. Deactivating
 * never unassigns (`services/tracking/actions/tags.py`); deleting does.
 */
import { EllipsisIcon, LockIcon, PlusIcon, Trash2Icon, TriangleAlertIcon } from 'lucide-react';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { ConfirmDialog, FormDialog } from '../../../chrome/form';
import { failureSentence } from '../../../chrome/state';
import { HintedIcon, IconHint } from '../../../chrome/hint';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Chip } from '../../../ui/chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { Field, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Switch } from '../../../ui/switch';
import {
  useCreateValue,
  useDeleteCategory,
  useDeleteValue,
  useUpdateCategory,
  useUpdateValue,
} from './api/queries';
import {
  ALIGNMENT_KEYS,
  ANALYSIS_SCOPES,
  type AlignmentKey,
  type AnalysisScope,
  type TagCategory,
  type TagValue,
} from './api/schemas';
import { ALIGNMENT_TONE, DEFAULT_TAG_ICON, IconPicker, TagGlyph, tagIconName } from './icons';
import { tgp, tgt } from './messages';

/** The locked value's name for each key, so "counts as" reads in the tenant's own words. */
function keyLabels(category: TagCategory): Record<AlignmentKey, string> {
  const named = Object.fromEntries(
    category.values.filter((v) => v.locked && v.alignment).map((v) => [v.alignment, v.name]),
  ) as Partial<Record<AlignmentKey, string>>;
  return Object.fromEntries(ALIGNMENT_KEYS.map((key) => [key, named[key] ?? key])) as Record<
    AlignmentKey,
    string
  >;
}

/** Non-blocking: tags describe accounts, and a name like "Religião" or "Partido do vereador"
 *  reads as recording a person's religion, party, health or orientation. */
function NameWarning(): ReactNode {
  return (
    <HintedIcon label={tgt('row.warning')} className="text-destructive">
      <TriangleAlertIcon aria-hidden className="size-3" />
    </HintedIcon>
  );
}

function AddValueForm(props: { campaignId: string; category: TagCategory }): ReactNode {
  const { category } = props;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [alignment, setAlignment] = useState<AlignmentKey>('ALLY');
  const createValue = useCreateValue(props.campaignId);
  const inputId = useId();
  const labels = keyLabels(category);

  function close(): void {
    setOpen(false);
    setName('');
    createValue.reset();
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '' || createValue.isPending) return;
    createValue.mutate(
      {
        categoryId: category.id,
        name: trimmed,
        ...(category.locked ? { alignment } : {}),
      },
      { onSuccess: close },
    );
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-footnote h-6 rounded-full px-2"
        onClick={() => setOpen(true)}
      >
        <PlusIcon aria-hidden className="size-3" />
        {tgt('row.addValue')}
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-1">
      <label htmlFor={inputId} className="sr-only">
        {tgt('row.addValueLabel', { category: category.name })}
      </label>
      <Input
        id={inputId}
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') close();
        }}
        className="text-footnote h-6 w-32"
      />
      {category.locked && (
        <Select value={alignment} onValueChange={(next) => setAlignment(next as AlignmentKey)}>
          <SelectTrigger size="sm" className="text-footnote h-6" aria-label={tgt('value.countsAs')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ALIGNMENT_KEYS.map((key) => (
              <SelectItem key={key} value={key}>
                {tgt('value.countsAsOption', { value: labels[key] })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <IconHint label={tgt('row.addValueSubmit')}>
        <Button
          type="submit"
          size="icon"
          variant="ghost"
          className="size-6"
          disabled={createValue.isPending || name.trim() === ''}
        >
          <PlusIcon aria-hidden className="size-3" />
        </Button>
      </IconHint>
      <Button type="button" size="sm" variant="ghost" className="h-6 px-2" onClick={close}>
        {tgt('row.addValueCancel')}
      </Button>
    </form>
  );
}

function ValueChip(props: {
  campaignId: string;
  category: TagCategory;
  value: TagValue;
  canWrite: boolean;
}): ReactNode {
  const { category, value } = props;
  const update = useUpdateValue(props.campaignId);
  const remove = useDeleteValue(props.campaignId);
  const [removing, setRemoving] = useState(false);
  const tone = value.alignment ? ALIGNMENT_TONE[value.alignment] : 'neutral';
  const chip = (
    <Chip
      size="sm"
      tone={tone}
      icon={<TagGlyph name={tagIconName(value, category)} />}
      className={value.active ? undefined : 'opacity-60'}
    >
      {value.name}
      {value.locked && <LockIcon aria-hidden className="size-2.5 opacity-60" />}
      {value.warning && <NameWarning />}
    </Chip>
  );
  if (!props.canWrite) return chip;

  return (
    <>
      <IconPicker
        value={value.icon ?? null}
        fallback={tagIconName(null, category)}
        label={tgt('icon.value', { name: value.name })}
        inheritLabel={tgt('icon.inheritCategory')}
        onChange={(icon) => update.mutate({ valueId: value.id, icon })}
        trigger={
          <button
            type="button"
            aria-label={tgt('icon.value', { name: value.name })}
            className="focus-visible:ring-ring/50 rounded-full outline-none focus-visible:ring-3"
          >
            {chip}
          </button>
        }
      >
        {value.alignment && (
          <div className="flex flex-col gap-1 px-2 py-1.5">
            <span className="text-caption font-medium">{tgt('scope.label')}</span>
            <Select
              value={value.analysis_scope ?? 'RELATED'}
              onValueChange={(scope) =>
                update.mutate({ valueId: value.id, analysis_scope: scope as AnalysisScope })
              }
            >
              <SelectTrigger size="sm" aria-label={tgt('scope.label')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANALYSIS_SCOPES.map((scope) => (
                  <SelectItem key={scope} value={scope}>
                    {tgt(`scope.${scope}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-caption text-muted-foreground">{tgt('scope.help')}</span>
          </div>
        )}
        {!value.locked && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive w-full justify-start"
            onClick={() => setRemoving(true)}
          >
            <Trash2Icon aria-hidden className="size-4" />
            {tgt('value.remove')}
          </Button>
        )}
      </IconPicker>
      <ConfirmDialog
        open={removing}
        onOpenChange={(open) => {
          setRemoving(open);
          if (!open) remove.reset();
        }}
        title={tgt('remove.title', { name: value.name })}
        description={tgt(value.alignment ? 'remove.alignmentValue' : 'remove.value', {
          fallback: keyLabels(category)[value.alignment ?? 'NEUTRAL'],
        })}
        confirmLabel={tgt('remove.submit')}
        destructive
        pending={remove.isPending}
        failure={remove.error?.failure ?? null}
        onConfirm={() => remove.mutateAsync(value.id)}
      />
    </>
  );
}

function RenameCategoryDialog(props: {
  campaignId: string;
  category: TagCategory;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { category } = props;
  const update = useUpdateCategory(props.campaignId);
  const [name, setName] = useState(category.name);
  const nameId = useId();

  function handleOpenChange(next: boolean): void {
    props.onOpenChange(next);
    if (!next) {
      setName(category.name);
      update.reset();
    }
  }

  function submit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '' || trimmed === category.name) return;
    return update.mutateAsync({ categoryId: category.id, name: trimmed });
  }

  return (
    <FormDialog
      open={props.open}
      onOpenChange={handleOpenChange}
      title={tgt('rename.title', { category: category.name })}
      submitLabel={tgt('rename.submit')}
      pending={update.isPending}
      submitDisabled={name.trim() === '' || name.trim() === category.name}
      failure={update.error?.failure ?? null}
      onSubmit={submit}
    >
      <Field>
        <FieldLabel htmlFor={nameId}>{tgt('rename.label')}</FieldLabel>
        <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
    </FormDialog>
  );
}

function CategoryRow(props: {
  campaignId: string;
  category: TagCategory;
  canWrite: boolean;
}): ReactNode {
  const { campaignId, category, canWrite } = props;
  const [renaming, setRenaming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const update = useUpdateCategory(campaignId);
  // Its own mutation, so the refusal renders under this row and nowhere else.
  const setSingle = useUpdateCategory(campaignId);
  const remove = useDeleteCategory(campaignId);
  const singleId = useId();

  return (
    <div className="flex flex-col gap-3 border-t py-3 first:border-t-0 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
      <div className="flex min-w-0 items-center gap-3 sm:w-56 sm:shrink-0">
        {canWrite ? (
          <IconPicker
            value={category.icon ?? null}
            fallback={DEFAULT_TAG_ICON}
            label={tgt('icon.category', { name: category.name })}
            inheritLabel={tgt('icon.inheritDefault')}
            onChange={(icon) => update.mutate({ categoryId: category.id, icon })}
          />
        ) : (
          <span className="bg-secondary inline-flex size-9 shrink-0 items-center justify-center rounded-md">
            <TagGlyph name={tagIconName(null, category)} className="size-4" />
          </span>
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-body flex items-center gap-1.5 font-medium">
            <span className="truncate">{category.name}</span>
            {category.warning && <NameWarning />}
            {category.locked && (
              <HintedIcon label={tgt('row.locked')} className="text-muted-foreground">
                <LockIcon aria-hidden className="size-3.5" />
              </HintedIcon>
            )}
          </span>
          <span className="text-caption text-muted-foreground">
            {category.active
              ? tgp('row.accounts.other', category.accounts, { count: category.accounts })
              : tgt('row.unused')}
          </span>
        </div>
      </div>
      <div className="flex flex-1 flex-wrap items-center gap-1.5">
        {category.values.map((value) => (
          <ValueChip
            key={value.id}
            campaignId={campaignId}
            category={category}
            value={value}
            canWrite={canWrite}
          />
        ))}
        {canWrite && <AddValueForm campaignId={campaignId} category={category} />}
      </div>
      {canWrite && !category.locked && (
        <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
          <DropdownMenu>
            <IconHint label={tgt('row.actionsMenu', { category: category.name })}>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon">
                  <EllipsisIcon aria-hidden />
                </Button>
              </DropdownMenuTrigger>
            </IconHint>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setRenaming(true)}>
                {tgt('row.rename')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(true)}>
                <Trash2Icon aria-hidden />
                {tgt('row.remove')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Label htmlFor={singleId} className="text-caption text-muted-foreground font-normal">
            {tgt('row.single')}
          </Label>
          <Switch
            id={singleId}
            checked={category.single === true}
            disabled={setSingle.isPending}
            onCheckedChange={(single) => setSingle.mutate({ categoryId: category.id, single })}
          />
          <Switch
            checked={category.active}
            onCheckedChange={(active) => update.mutate({ categoryId: category.id, active })}
            aria-label={tgt('row.active', { category: category.name })}
          />
        </div>
      )}
      {setSingle.error && (
        <p role="alert" className="text-caption text-destructive sm:basis-full">
          {failureSentence(setSingle.error.failure)}
        </p>
      )}
      {renaming && (
        <RenameCategoryDialog
          campaignId={campaignId}
          category={category}
          open
          onOpenChange={setRenaming}
        />
      )}
      <ConfirmDialog
        open={removing}
        onOpenChange={(open) => {
          setRemoving(open);
          if (!open) remove.reset();
        }}
        title={tgt('remove.title', { name: category.name })}
        description={tgp('remove.category.other', category.accounts, {
          count: category.accounts,
        })}
        confirmLabel={tgt('remove.submit')}
        destructive
        pending={remove.isPending}
        failure={remove.error?.failure ?? null}
        onConfirm={() => remove.mutateAsync(category.id)}
      />
    </div>
  );
}

export function CategoryPanel(props: {
  title: string;
  subtitle: string;
  categories: TagCategory[];
  campaignId: string;
  canWrite: boolean;
  footer?: ReactNode;
}): ReactNode {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="flex flex-row items-center justify-between border-b py-3">
        <CardTitle className="text-body">{props.title}</CardTitle>
        <span className="text-caption text-muted-foreground">{props.subtitle}</span>
      </CardHeader>
      <CardContent className="flex flex-col px-4 py-2">
        {props.categories.map((category) => (
          <CategoryRow
            key={category.id}
            campaignId={props.campaignId}
            category={category}
            canWrite={props.canWrite}
          />
        ))}
        {props.footer}
      </CardContent>
    </Card>
  );
}
